import { noneDecimalFormatter } from '@/Helper';
import { deviceAdminApi } from '@/api';
import { InformationMessages, ModalConfirmation, ModalWarning } from '@/components/common';
import { SubPermissions, User } from '@/model/AccountModel';
import { RS485ReaderType, ReaderError } from '@/model/DeviceAdminModel';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, InputNumber } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { DoorStoreSingleContext } from '../../../../../DoorModal/DoorStoreSingleContext';
import { UpdateFirmwareModal } from '../../../../UpdateFirmwareModal/UpdateFirmwareModal';
import { StoreContext, setOSDPAddress, setOkDegrade, setReaderErrorType } from '../../../../contextReader';
import styles from '../../../General/general.module.scss';

type Props = {
	handleDownloadFirmware: () => void;
	useDoorContext: boolean;
};

type VersionProps = {
	rS485ReaderType: number;
	firmware: string;
	appVersion: string;
	displayBoardVersion: string;
};

export const ReaderVersions: React.FC<VersionProps> = ({ rS485ReaderType, firmware, appVersion, displayBoardVersion }) => {
	let showAPPVersion = false;
	let showDisplayBoardVersion = false;

	switch (rS485ReaderType as RS485ReaderType) {
		case RS485ReaderType.TS:
		case RS485ReaderType.TSKeypad:
		case RS485ReaderType.TSScramblePad:
		case RS485ReaderType.TSPIVAUTH:
			showAPPVersion = true;
			break;

		case RS485ReaderType.TS_SF1:
		case RS485ReaderType.TS_SF2:
		case RS485ReaderType.TS_SF3:
			showAPPVersion = true;
			showDisplayBoardVersion = true;
			break;
	}

	return (
		<div className={styles.versionsContainer}>
			<span>{_('TRNVersion')}:</span>
			<span>{firmware}</span>
			{showAPPVersion && (
				<>
					<span>{_('APPVersion')}:</span>
					<span>{appVersion}</span>
				</>
			)}
			{showDisplayBoardVersion && (
				<>
					<span>{_('DisplayVersion')}:</span>
					<span>{displayBoardVersion}</span>
				</>
			)}
		</div>
	);
};

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const userCanUpdateFirmware = User.getSubComponentPermission(user, SubPermissions.Reader_FirmwareUpdateTSReader).allowed;
const style: React.CSSProperties = { color: '#faad14' };

const RS485Body: React.FC<Props> = ({ handleDownloadFirmware, useDoorContext }) => {
	//#region State Management Variables
	if (useDoorContext) {
		var {
			contextStateDoor: {
				entryReader: {
					isFICAMEnabled,
					updateFirmwarePermissionText,
					canUpdateFirmware,
					oSDPAddress,
					firmware,
					appVersion,
					displayBoardVersion,
					okDegrade,
					readerErrorMessage,
					readerErrorType,
					selections: { rS485ReaderType },
					isVeridtReaderUpdateAllowed,
				},
			},
			dispatcherDoor,
		} = useContext(DoorStoreSingleContext);
	} else {
		var {
			readerState: {
				isFICAMEnabled,
				updateFirmwarePermissionText,
				canUpdateFirmware,
				oSDPAddress,
				firmware,
				appVersion,
				displayBoardVersion,
				okDegrade,
				selections: { rS485ReaderType },
				isVeridtReaderUpdateAllowed,
			},
			dispatcher,
		} = useContext(StoreContext);
	}

	const [showModal, setShowModal] = useState(false);
	const [OSDPAddressErrorMessage, setOSDPAddressErrorMessage] = useState<JSX.Element>(<></>);
	const [firmwareTitleMessage, setFirmwareTitleMessage] = useState<string>();
	const [warningMessage, setWarningMessage] = useState<string[]>();
	const [isVeridtReader, setIsVeridtReader] = useState<boolean>(false);
	//#endregion

	useEffect(() => {
		if (readerErrorType === ReaderError.OSDPAddress) {
			setOSDPAddressErrorMessage(<span className={styles.errorMessage}>{readerErrorMessage}</span>);
		} else {
			setOSDPAddressErrorMessage(<></>);
		}
	}, [readerErrorType]);

	useEffect(() => {
		setIsVeridtReader(rS485ReaderType === RS485ReaderType.VeridtPIVAUTH || rS485ReaderType === RS485ReaderType.VeridtPIVAUTHBIO);
		fetchModalMessages();
	}, [rS485ReaderType]);

	const handleOnChangeAddress = (value: React.Key) => {
		if (value === '' || value === null || !Number(value.toString())) {
			if (value != 0) {
				return;
			}
		}

		useDoorContext ? dispatcherDoor(setOSDPAddress(Number(value.toString()))) : dispatcher(setOSDPAddress(Number(value.toString())));
		useDoorContext ? dispatcherDoor(setReaderErrorType(undefined)) : null;
	};

	const fetchModalMessages = async () => {
		const firmwareConfirmResponse = await deviceAdminApi.getFirmwareConfirmString(rS485ReaderType);
		const firmwareTitleResponse = await deviceAdminApi.getFirmwareTitleString(rS485ReaderType);
		setFirmwareTitleMessage(firmwareTitleResponse.Entity);
		setWarningMessage(firmwareConfirmResponse.Entity.split('\n'));
	};

	const handleOnClickUpdateFirmware = async () => {
		if (userCanUpdateFirmware) {
			const content: JSX.Element = <InformationMessages messages={warningMessage} />;

			ModalConfirmation({
				title: firmwareTitleMessage,
				content: content,
				onConfirm: () => setShowModal(true),
				okText: _('Yes'),
				cancelText: _('No'),
				width: '700px',
				icon: <ExclamationCircleOutlined style={style} />,
			});
		} else {
			const content: JSX.Element = <InformationMessages messages={[updateFirmwarePermissionText]} />;

			ModalWarning({
				okText: _('Ok'),
				content: content,
				width: '600px',
			});
		}
	};
	const handleOnSetOkDegrade = (value: CheckboxChangeEvent) => {
		useDoorContext ? dispatcherDoor(setOkDegrade(value.target.checked)) : dispatcher(setOkDegrade(value.target.checked));
	};

	const handleOnCloseUpdateFirmwareModal = () => {
		setShowModal(false);
	};

	const showUpdateFirmwareButton = (canUpdateFirmware && !isVeridtReader) || (canUpdateFirmware && isVeridtReader && isVeridtReaderUpdateAllowed);

	return (
		<>
			<div className={styles.sections}>
				<span>{_('OSDPAddress')}</span>
				<div>
					<InputNumber
						key='osdpAddress'
						min={0}
						max={126}
						maxLength={3}
						value={oSDPAddress}
						onChange={handleOnChangeAddress}
						id={'osdpAddress'}
						className={cx(styles.input, {
							[styles.error]: readerErrorType === ReaderError.OSDPAddress,
						})}
						formatter={noneDecimalFormatter}
					/>
					{OSDPAddressErrorMessage}
				</div>
			</div>
			{isFICAMEnabled && (
				<div className={styles.sections}>
					<div className={styles.okDegrade}>
						<Checkbox checked={okDegrade} onChange={handleOnSetOkDegrade} id={'okDegrade'}>
							<span>{_('AllowAccessInDegrade')}</span>
						</Checkbox>
					</div>
				</div>
			)}
			<div className={styles.doubleColumn}>
				<ReaderVersions rS485ReaderType={rS485ReaderType} firmware={firmware} appVersion={appVersion} displayBoardVersion={displayBoardVersion} />
				<div className={styles.rightColumn}>
					{showUpdateFirmwareButton && (
						<Button
							key='updateFirmware'
							type='primary'
							id='readerModalUpdateReaderFirmwareButton'
							onClick={handleOnClickUpdateFirmware}
							disabled={rS485ReaderType === -1}>
							{_('UpdateReaderFirmware')}
						</Button>
					)}
				</div>
			</div>
			<UpdateFirmwareModal
				visible={showModal}
				firmwareTitle={firmwareTitleMessage}
				useDoorContext={useDoorContext}
				isVeridtReader={isVeridtReader}
				closeModal={handleOnCloseUpdateFirmwareModal}
				handleDownloadFirmware={handleDownloadFirmware}
			/>
		</>
	);
};

export { RS485Body };
